import {
  MouseEvent, FC, useState, useRef, useCallback, useMemo,
} from 'react';
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react';

import { SHARED_MEDIA_QUERIES as sharedMediaQueries } from 'src/common/constants';
import getClassNames from '../../utils/getClassNames';
import { useMediaQueries } from '../../providers/MediaQueryProvider';
import { CarouselProps } from './CarouselProps';
import styles from './Carousel.module.scss';
import ChevronButtons from '../ChevronButtons';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

const preventEventBubble = (fn: () => void) => (event?: MouseEvent<HTMLButtonElement>) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  fn?.();
};

const settings: EmblaOptionsType = {
  loop: false,
  align: 'start',
  inViewThreshold: 1,
  skipSnaps: false,
};

/**
 * This is intended to be lazily loaded by the `Carousel`.
 * Please do not use this directly.
 */
const EnhancedCarousel: FC<CarouselProps> = ({
  loop,
  children,
  classNameModifier,
  slidesToScroll,
  preventEdgeScrolling = false,
  largeBreakpoint = sharedMediaQueries.l,
  containScroll = 'trimSnaps',
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...settings,
    slidesToScroll,
    containScroll,
  });
  const mediaQueries = useMediaQueries();
  const isLargeDevice = mediaQueries[largeBreakpoint as keyof typeof mediaQueries];
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const intersectionObserverRef = useRef();
  const renderControls = useMemo(
    () => canScrollNext || canScrollPrev,
    [canScrollNext, canScrollPrev],
  );

  const onScroll = useCallback(() => {
    if (!emblaApi) return;

    if (preventEdgeScrolling) {
      // A known issue that does not have an option associated to it
      // This is the advised workaround as per embla-carousel issue #42
      // See: https://github.com/davidjerleke/embla-carousel/issues/42#issuecomment-1287131957
      const {
        limit, target, location, scrollTo,
      } = emblaApi.internalEngine();
      if (limit.reachedMax(target.get())) {
        if (limit.reachedMax(location.get())) location.set(limit.max);
        target.set(limit.max);
        scrollTo.distance(0, false);
      }
      if (limit.reachedMin(target.get())) {
        if (limit.reachedMin(location.get())) location.set(limit.min);
        target.set(limit.min);
        scrollTo.distance(0, false);
      }
    }

    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi, preventEdgeScrolling]);

  const calculateNumberOfSlides = useCallback(() => {
    const entirelyVisibleItems = emblaApi.slidesInView().length;
    emblaApi.reInit({
      ...settings,
      loop,
      slidesToScroll: isLargeDevice ? slidesToScroll : Math.max(entirelyVisibleItems, 1),
      align: isLargeDevice ? 'start' : 'center',
    });
    onScroll();
  }, [emblaApi, isLargeDevice, slidesToScroll, loop, onScroll]);

  const carouselInit = useCallback(() => {
    if (emblaApi) {
      emblaApi.on('scroll', onScroll);
      emblaApi.on('resize', calculateNumberOfSlides);
      calculateNumberOfSlides();
    }
  }, [emblaApi, onScroll, calculateNumberOfSlides]);

  useIntersectionObserver({ ref: intersectionObserverRef, callback: carouselInit });

  return (
    <div
      className={getClassNames(styles, 'container', classNameModifier)}
      ref={intersectionObserverRef}
    >
      <div
        className={getClassNames(styles, 'viewport', classNameModifier)}
        ref={emblaRef}
      >
        <ul className={getClassNames(styles, 'list', classNameModifier)}>{children}</ul>
        {renderControls && (
          <ChevronButtons
            previousDisabled={!canScrollPrev}
            nextDisabled={!canScrollNext}
            previous={preventEventBubble(emblaApi?.scrollPrev)}
            next={preventEventBubble(emblaApi?.scrollNext)}
            classNameModifier={classNameModifier}
          />
        )}
      </div>
    </div>
  );
};

export default EnhancedCarousel;
